<script setup>
/**
 * OCHAT: la configuración del hotel — cupos, tarifas y catálogo.
 *
 * PERMISO DE ADMINISTRADOR DE LA CUENTA, NO DE SUPER ADMIN
 * Bandejas, integraciones y automatizaciones sí están reservadas al operador: son
 * infraestructura de la plataforma. Las tarifas y el cupo son del NEGOCIO del
 * cliente. El dueño de un hotel tiene que poder subir el precio de un fin de semana
 * sin pedirle permiso a nadie. La ruta declara `permissions: ['administrator']` y las
 * policies del servidor exigen `@account_user.administrator?`: son el mismo permiso
 * dicho dos veces, para que la pantalla no ofrezca algo que el servidor rechaza.
 *
 * TRES PESTAÑAS Y NO UNA COLUMNA APILADA
 * Antes las tres partes —cargar temporada, catálogo y datos del hotel— iban una
 * debajo de la otra en el mismo scroll. Son tareas de frecuencia muy distinta: la
 * temporada se carga todas las semanas, el catálogo una vez por hotel, y los datos
 * del hotel casi nunca. Apiladas se leían como un solo formulario gigante y no se
 * entendía dónde terminaba una y empezaba la otra.
 *
 * Son pestañas y no un asistente de pasos porque no es una configuración de una sola
 * vez: se vuelve a ella todo el tiempo, y un asistente obliga a atravesar pasos ya
 * resueltos. Lo único que se toma prestado del asistente es el orden: si todavía no
 * hay tipos de habitación, se entra por el catálogo, porque sin él la carga por rango
 * no tiene sobre qué aplicarse.
 *
 * Las pestañas de acá son pastillas y no subrayados a propósito: arriba ya hay una
 * barra de pestañas subrayadas y dos filas del mismo control se confunden entre sí.
 */
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import PropertyForm from '../components/PropertyForm.vue';
import RoomTypeManager from '../components/RoomTypeManager.vue';
import BulkCalendarForm from '../components/BulkCalendarForm.vue';

const store = useStore();
const { t } = useI18n();
const properties = useMapGetter('bookingSettings/getProperties');
const uiFlags = useMapGetter('bookingSettings/getUIFlags');

const selectedId = ref(null);
const section = ref('season');
// Para no volver a mandar al catálogo cada vez que se recarga la propiedad si el
// usuario ya eligió ir a otra parte.
const sectionChosenByUser = ref(false);

onMounted(() => store.dispatch('bookingSettings/get'));

// Se elige la primera sola: casi siempre hay una. El selector aparece solo si hay más.
watch(
  properties,
  list => {
    if (!list.length) {
      selectedId.value = null;
      return;
    }
    const stillThere = list.some(item => item.id === selectedId.value);
    if (!stillThere) selectedId.value = list[0].id;
  },
  { immediate: true }
);

const property = computed(() =>
  properties.value.find(item => item.id === selectedId.value)
);

const hasRoomTypes = computed(
  () => (property.value?.room_types?.length ?? 0) > 0
);

const SECTIONS = [
  { key: 'season', label: 'BOOKINGS.SETTINGS.SECTION.SEASON' },
  { key: 'catalog', label: 'BOOKINGS.SETTINGS.SECTION.CATALOG' },
  { key: 'hotel', label: 'BOOKINGS.SETTINGS.SECTION.HOTEL' },
];

const sections = computed(() =>
  SECTIONS.map(item => ({ key: item.key, label: t(item.label) }))
);

const goTo = key => {
  section.value = key;
  sectionChosenByUser.value = true;
};

// Sin tipos de habitación cargados, entrar por "Cargar temporada" es entrar a un
// formulario que no se puede enviar. Se entra por el catálogo, salvo que el usuario
// ya haya elegido otra pestaña a mano.
watch(
  hasRoomTypes,
  has => {
    if (!has && !sectionChosenByUser.value) section.value = 'catalog';
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-6 max-w-4xl">
    <p
      v-if="uiFlags.isFetching && !properties.length"
      class="text-sm text-n-slate-11"
    >
      {{ $t('BOOKINGS.SETTINGS.LOADING') }}
    </p>

    <!-- Todavía no hay hotel cargado: lo único que se puede hacer es crearlo. -->
    <section v-else-if="!properties.length" class="flex flex-col gap-3">
      <h2 class="text-heading-2 text-n-slate-12">
        {{ $t('BOOKINGS.SETTINGS.PROPERTY.FIRST_TITLE') }}
      </h2>
      <p class="text-sm text-n-slate-11">
        {{ $t('BOOKINGS.SETTINGS.PROPERTY.FIRST_HINT') }}
      </p>
      <PropertyForm />
    </section>

    <template v-else-if="property">
      <div v-if="properties.length > 1" class="flex flex-col gap-1 max-w-xs">
        <label for="property-select" class="text-sm text-n-slate-11">
          {{ $t('BOOKINGS.SETTINGS.PROPERTY.SELECT') }}
        </label>
        <select
          id="property-select"
          v-model="selectedId"
          class="px-3 py-1.5 text-sm rounded-lg outline outline-1 outline-n-container bg-n-background text-n-slate-12"
        >
          <option v-for="item in properties" :key="item.id" :value="item.id">
            {{ item.name }}
          </option>
        </select>
      </div>

      <nav
        class="flex self-start gap-1 p-1 rounded-lg bg-n-alpha-1"
        :aria-label="$t('BOOKINGS.TABS.INVENTORY')"
      >
        <button
          v-for="item in sections"
          :key="item.key"
          type="button"
          :aria-current="section === item.key ? 'page' : undefined"
          class="px-3 py-1.5 text-sm font-medium rounded-lg"
          :class="
            section === item.key
              ? 'bg-n-background text-n-slate-12'
              : 'text-n-slate-11 hover:text-n-slate-12'
          "
          @click="goTo(item.key)"
        >
          {{ item.label }}
        </button>
      </nav>

      <!-- Cargar temporada -->
      <section v-if="section === 'season'" class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-heading-2 text-n-slate-12">
            {{ $t('BOOKINGS.SETTINGS.BULK.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.SUBTITLE') }}
          </p>
        </div>

        <!--
          Sin tipos de habitación no hay nada sobre lo que cargar cupo ni tarifa, así
          que en lugar de un formulario muerto se dice qué falta y se ofrece el atajo.
        -->
        <div
          v-if="!hasRoomTypes"
          class="flex flex-col items-start gap-3 p-4 rounded-lg bg-n-alpha-1"
        >
          <p class="text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.BULK.NEEDS_CATALOG') }}
          </p>
          <button
            type="button"
            class="px-3 py-1.5 text-sm font-medium rounded-lg bg-n-brand text-white"
            @click="goTo('catalog')"
          >
            {{ $t('BOOKINGS.SETTINGS.BULK.GO_TO_CATALOG') }}
          </button>
        </div>

        <BulkCalendarForm v-else :property="property" />
      </section>

      <!-- Habitaciones y tarifas -->
      <section v-else-if="section === 'catalog'" class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-heading-2 text-n-slate-12">
            {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{
              hasRoomTypes
                ? $t('BOOKINGS.SETTINGS.ROOM_TYPE.SUBTITLE')
                : $t('BOOKINGS.SETTINGS.ROOM_TYPE.FIRST_HINT')
            }}
          </p>
        </div>
        <RoomTypeManager :property="property" />

        <!--
          El atajo de vuelta aparece recién cuando hay algo cargado: antes no tendría
          adónde llevar.
        -->
        <div v-if="hasRoomTypes" class="pt-2">
          <button
            type="button"
            class="text-sm text-n-brand hover:underline"
            @click="goTo('season')"
          >
            {{ $t('BOOKINGS.SETTINGS.ROOM_TYPE.GO_TO_SEASON') }}
          </button>
        </div>
      </section>

      <!-- Datos del hotel -->
      <section v-else class="flex flex-col gap-3">
        <div class="flex flex-col gap-1">
          <h2 class="text-heading-2 text-n-slate-12">
            {{ $t('BOOKINGS.SETTINGS.PROPERTY.TITLE') }}
          </h2>
          <p class="text-sm text-n-slate-11">
            {{ $t('BOOKINGS.SETTINGS.PROPERTY.SUBTITLE') }}
          </p>
        </div>
        <PropertyForm :key="property.id" :property="property" />
      </section>
    </template>
  </div>
</template>
